import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/browser';

const RETRY_DELAY_MS = 2000;
export const STRIPE_LOAD_TIMEOUT_MS = 20_000;

/**
 * Deferred Stripe loader with a timeout and a single automatic retry.
 *
 * Unlike calling loadStripe() at module scope (which fires during route
 * preloading and affects every visitor), this only attempts to load Stripe
 * when a payment page actually mounts.
 *
 * Flow:
 *  1. First call → attempts loadStripe(), raced against a 20 s timeout
 *  2. If it ERRORS → waits 2 s, retries once
 *  3. If it TIMES OUT → rejects immediately, kind 'timeout' (no retry — see below)
 *  4. If the retry fails → rejects (caller shows fallback UI + Sentry feedback)
 *  5. Subsequent calls while a load is in-flight return the same promise
 *  6. After a failure, the next call starts fresh here (allows manual "Try Again")
 *
 * ⚠️ WHY A TIMEOUT IS NOT RETRIED (CU-17tm466h6uv). Before this, a script that
 * never fired `load` or `error` hung forever: no retry, no error UI, no feedback
 * form. But @stripe/stripe-js (v7.9.0, `dist/pure.js` loadScript) caches its
 * PENDING load promise and resets it only on ERROR — so calling loadStripe()
 * again after OUR timeout returns the SAME hung promise. Neither an automatic
 * retry nor "Try Again" can recover from a hang; only a page reload can. The
 * caller reads `kind === 'timeout'` and offers a reload instead.
 *
 * Refs: MIDEO-UNIFIED-1P, MIDEO-UNIFIED-C2
 */

export type StripeLoadErrorKind = 'timeout' | 'failed';

export class StripeLoadError extends Error {
  readonly kind: StripeLoadErrorKind;

  constructor(kind: StripeLoadErrorKind, message: string) {
    super(message);
    this.name = 'StripeLoadError';
    this.kind = kind;
  }
}

let pending: Promise<Stripe | null> | null = null;

/**
 * Sequence for the timeout capture — Sentry's Dedupe drops an event identical to
 * the previous one (message + stack), so a pre-load timeout followed by a
 * checkout timeout in the same page would otherwise report once. Same approach
 * as getPaymentIntent.ts.
 */
let timeoutSequence = 0;

/** Exported for tests — module state would otherwise leak between them. */
export function __resetStripeLoaderForTest(): void {
  pending = null;
  timeoutSequence = 0;
}

function attemptLoad(): Promise<Stripe | null> {
  return loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout(load: Promise<Stripe | null>): Promise<Stripe | null> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      // Starts with "Failed to load Stripe.js" on purpose: App.tsx's beforeSend
      // drops that phrase for bot user agents, and a timeout is the same event.
      reject(new StripeLoadError('timeout', `Failed to load Stripe.js: timed out after ${STRIPE_LOAD_TIMEOUT_MS}ms`));
    }, STRIPE_LOAD_TIMEOUT_MS);
    load.then(
      (stripe) => {
        clearTimeout(timer);
        resolve(stripe);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function isTimeout(error: unknown): boolean {
  return error instanceof StripeLoadError && error.kind === 'timeout';
}

function captureTimeout(error: StripeLoadError): void {
  timeoutSequence += 1;
  const message = timeoutSequence === 1 ? error.message : `${error.message} (#${timeoutSequence})`;
  Sentry.captureException(new Error(message), {
    fingerprint: ['stripe-load', 'timeout'],
    tags: { stripe_load: 'timeout' },
  });
}

export function getStripePromise(): Promise<Stripe | null> {
  if (pending) return pending;

  pending = withTimeout(attemptLoad()).catch(async (firstError) => {
    if (isTimeout(firstError)) {
      captureTimeout(firstError as StripeLoadError);
      throw firstError;
    }

    // Automatic retry after delay (a real ERROR resets stripe-js's own cache,
    // so this genuinely re-injects the script)
    await delay(RETRY_DELAY_MS);
    try {
      return await withTimeout(attemptLoad());
    } catch (retryError) {
      // Both attempts failed — report to Sentry and reject
      Sentry.captureException(retryError, {
        tags: { stripe_retry: 'exhausted' },
        contexts: {
          stripe_load: {
            first_error: firstError?.message ?? String(firstError),
            retry_error: (retryError as Error)?.message ?? String(retryError),
          },
        },
      });
      if (retryError instanceof StripeLoadError) throw retryError;
      throw new StripeLoadError('failed', (retryError as Error)?.message ?? String(retryError));
    }
  });

  // Reset on failure so the next call (e.g. manual "Try Again") starts fresh
  pending.catch(() => {
    pending = null;
  });

  return pending;
}
